import { useState, type ReactNode } from 'react';
import { T, Var } from 'gt-react';
import type { PurchaseTrendAppliedMapping } from '@erp/rpc';
import { RebateAnalyticsCollapseToggle } from '@/components/rebates/RebateAnalyticsCollapseToggle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function appliedMappingSource(row: PurchaseTrendAppliedMapping): string {
  if (row.kind === 'division') {
    return row.sourceBranch
      ? `${row.sourceName} / ${row.sourceBranch}`
      : row.sourceName;
  }
  return row.sourceCode
    ? `${row.sourceCode} ${row.sourceName}`
    : row.sourceName;
}

export function PurchaseTrendCollapsibleCard({
  testId,
  collapseTestId,
  title,
  summary,
  children,
}: {
  testId: string;
  collapseTestId: string;
  title: ReactNode;
  summary?: ReactNode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(true);
  return (
    <Card className="mt-3" data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-1.5">
          <RebateAnalyticsCollapseToggle
            isCollapsed={collapsed}
            onToggle={() => setCollapsed((open) => !open)}
            testId={collapseTestId}
          />
          {title}
          {summary ? <span className="ml-auto">{summary}</span> : null}
        </CardTitle>
      </CardHeader>
      {collapsed ? null : (
        <CardContent className="space-y-3 text-sm">{children}</CardContent>
      )}
    </Card>
  );
}

export function PurchaseTrendAppliedMappingsSection({
  rows,
}: {
  rows: PurchaseTrendAppliedMapping[];
}) {
  if (rows.length === 0) {
    return null;
  }
  const divisions = rows.filter((row) => row.kind === 'division');
  const suppliers = rows.filter((row) => row.kind === 'supplier');
  return (
    <PurchaseTrendCollapsibleCard
      testId="applied-purchase-mappings"
      collapseTestId="applied-purchase-mappings-collapse"
      title={
        <T context="title for saved purchase file mappings used in the current view">
          Saved mappings used in this file
        </T>
      }
    >
      <p className="text-muted-foreground">
        <T context="where to remove a saved purchase file mapping">
          These are saved as aliases. To remove one, open the division or
          supplier company and delete the alias.
        </T>
      </p>
      {divisions.length > 0 ? (
        <div data-testid="applied-purchase-mappings-divisions">
          <p className="font-medium">
            <T context="heading for saved purchase file division mappings">
              Divisions
            </T>
          </p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            {divisions.map((row) => (
              <li key={`${row.sourceName}|||${row.sourceBranch ?? ''}`}>
                <T context="saved purchase mapping from file name to Gently name">
                  <Var>{appliedMappingSource(row)}</Var> →{' '}
                  <Var>{row.targetName}</Var>
                </T>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {suppliers.length > 0 ? (
        <div data-testid="applied-purchase-mappings-suppliers">
          <p className="font-medium">
            <T context="heading for saved purchase file vendor mappings">
              Suppliers
            </T>
          </p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            {suppliers.map((row) => (
              <li key={`${row.sourceCode ?? ''}|||${row.sourceName}`}>
                <T context="saved purchase mapping from file name to Gently name">
                  <Var>{appliedMappingSource(row)}</Var> →{' '}
                  <Var>{row.targetName}</Var>
                </T>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </PurchaseTrendCollapsibleCard>
  );
}
